import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "../../lib/api";
import {
  Banner, Card, CopyField, Empty, Field, Modal, Pager, Stat, Switch, Tabs,
  copyText, inr, inrCompact, useDebounced, usePagedList, useSyncFilter, usePolling,
} from "../../components/sc/ui";
import { TxList, TxToolbar, TxnDrawer, errMsg, exportCsv } from "./gateway/common";
import { TablePager } from "../../components/sc/Pager";

const STATUSES = ["all", "initiated", "pending", "success", "failed", "dropped", "dispute", "error"];

// Merchant on/off for Auto Pay routing; the "from ₹" amount is set by SuperCash (god mode).
function RoutingCard({ profile, onSaved }) {
  const [busy, setBusy] = useState(false);
  const on = !!profile.p2g_auto;
  const from = profile.p2g_auto_from || 0;
  const toggle = async (next) => {
    setBusy(true);
    try {
      const { data } = await api.put("/gateway/routing", { enabled: next });
      onSaved(data);
      toast.success(next ? "Auto Pay routing on" : "Auto Pay routing off");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Card title="Auto Pay routing" sub={on
      ? `On — orders of ${inr(from)} or more go through Auto Pay. Smaller ones use the P2M / P2P mode you send.`
      : "Off — every order uses the mode you send (P2M, P2P or P2G)."}
      actions={<Switch testId="gw-routing-switch" on={on} disabled={busy} onChange={toggle} />}>
      <div className="ui-mono ui-muted" style={{ fontSize: 12 }}>
        Auto Pay from <b>{from ? inr(from) : "any amount"}</b> · set by SuperCash. Your integration doesn't change — the order response's <code>payment_mode</code> shows the mode used.
      </div>
    </Card>
  );
}

export default function PayinGateway() {
  const navigate = useNavigate();
  const [tab, setTab] = useState("txns");
  const [profile, setProfile] = useState(null);
  const [summary, setSummary] = useState(null);

  const loadProfile = useCallback(() => api.get("/gateway/profile").then((r) => setProfile(r.data)).catch(() => {}), []);
  const loadSummary = useCallback(() => api.get("/gateway/summary").then((r) => setSummary(r.data)).catch(() => {}), []);
  useEffect(() => { loadProfile(); loadSummary(); }, [loadProfile, loadSummary]);
  usePolling(loadSummary, 8000);

  const t = summary?.payins_today || {};
  const all = summary?.payins || {};
  const sum = (o, keys) => keys.reduce((a, k) => ({ c: a.c + (o[k]?.count || 0), v: a.v + (o[k]?.amount || 0) }), { c: 0, v: 0 });
  const todayOk = sum(t, ["success"]);
  const todayAll = sum(t, STATUSES.slice(1));
  const open = sum(all, ["initiated", "pending"]);
  const rate = todayAll.c ? Math.round((todayOk.c / todayAll.c) * 100) : null;

  return (
    <div className="ui-page" data-testid="payin-gateway-page">
      {profile && !profile.gateway_live && <Banner>The payment gateway is being set up by SuperCash. You'll be able to collect here once it's live.</Banner>}
      {profile && profile.gateway_live && !profile.payin_enabled && (
        <Banner>Payins aren't enabled for your account yet. Contact SuperCash support to switch them on.</Banner>
      )}

      {profile?.payin_enabled && <RoutingCard profile={profile} onSaved={setProfile} />}

      <div className="ui-grid ui-grid-4">
        <Stat accent label="Collected today" value={inrCompact(todayOk.v)} foot={`${todayOk.c} successful`} testId="gw-stat-today" />
        <Stat label="Success rate today" value={rate == null ? "—" : `${rate}%`} foot={`${todayAll.c} attempts`} />
        <Stat label="Awaiting payment" value={open.c.toLocaleString("en-IN")} foot={inr(open.v)} testId="gw-stat-open" />
        <Stat label="Payout balance" value={inrCompact(summary?.balance)} foot="Send payouts →" onClick={() => navigate("/payout")} testId="gw-stat-balance" />
      </div>

      <div className="ui-between">
        <Tabs value={tab} onChange={setTab} testIdPrefix="gw-tab" tabs={[
          { key: "txns", label: "Transactions" },
          { key: "callbacks", label: "Callbacks" },
        ]} />
        {profile && (
          <span className="ui-mono ui-muted" style={{ fontSize: 11.5 }}>
            {profile.environment === "live" ? "● LIVE" : "● TEST MODE"}
          </span>
        )}
      </div>

      {tab === "txns" && <Payins enabled={!!profile?.payin_enabled && !!profile?.gateway_live} counts={summary?.payins} onChanged={loadSummary} />}
      {tab === "callbacks" && <WebhookNote url={profile?.webhook_url} />}
      {tab === "callbacks" && <RecentWebhooks />}
    </div>
  );
}

function Payins({ enabled, counts, onChanged }) {
  const [qInput, setQInput] = useState("");
  const q = useDebounced(qInput);
  const [openId, setOpenId] = useState(null);
  const list = usePagedList((p) => api.get("/gateway/payins", { params: p }).then((r) => r.data), { status: "all", q: "", date_from: "", date_to: "" }, 25);
  useSyncFilter(list.setFilter, "q", q);
  usePolling(list.load, 4000, list.page === 1);

  const rows = list.data.items.map((p) => ({
    id: p.id, status: p.status, at: p.created_at,
    title: p.order_id, sub: `P2G checkout · ${p.attempt_ref || ""}`,
    who: p.customer?.name || p.customer?.phone || p.customer?.email || p.customer?.id,
    whoSub: p.customer?.name ? p.customer?.phone || p.customer?.email : null,
    ref: p.utr, refSub: p.payer_vpa,
    amount: p.amount, amountSub: p.fee && p.net_amount ? `net ${inr(p.net_amount)}` : null,
  }));

  return (
    <Card pad={false} data-testid="gw-payins">
      <div className="ui-row" style={{ gap: 8, padding: "14px 16px 0" }}>
        <h3 className="ui-card-title">Payins</h3>
        <span className="ui-mono ui-faint" style={{ fontSize: 12 }}>{list.data.total.toLocaleString("en-IN")}</span>
      </div>
      <TxToolbar list={list} qInput={qInput} setQInput={setQInput} statuses={STATUSES} counts={counts}
        placeholder="Search order ID, UTR, VPA, phone…" onExport={() => exportCsv("/gateway/payins/export", list.filters)} />
      <div style={{ height: 10 }} />
      <TxList rows={rows} loading={list.loading} fresh={list.fresh} activeId={openId} onOpen={setOpenId}
        headers={["Order", "Customer", "UTR / VPA", "Time", "Amount", "Status"]}
        empty={<Empty icon="⚡" title="No payins found">{enabled ? "Every payment on a P2G order (API payment_mode \"p2g\" or a P2G payment link) appears here live." : "Payins appear here once Auto Pay is enabled."}</Empty>}
        footer={list.data.total > 0 && <Pager page={list.data.page} pages={list.data.pages} total={list.data.total} size={list.size} onPage={list.setPage} onSize={list.setSize} />} />
      {openId && <TxnDrawer kind="payin" id={openId} onClose={() => setOpenId(null)} onChanged={() => { list.load(); onChanged(); }} />}
    </Card>
  );
}

/** Auto Pay / Payout callbacks go to the merchant's normal webhook (Callbacks page). */
export function WebhookNote({ url, payout = false }) {
  const navigate = useNavigate();
  return (
    <Banner tone="info">
      <span>
        {payout
          ? <>Payout events go to your <b>Payout webhook</b> (or, if empty, your main webhook {url ? <b className="ui-mono" style={{ wordBreak: "break-all" }}>{url}</b> : <b>(not set yet)</b>})</>
          : <>P2G payments send your normal <span className="ui-mono">payment.success</span> / <span className="ui-mono">order.paid</span> webhooks to {url ? <b className="ui-mono" style={{ wordBreak: "break-all" }}>{url}</b> : <b>your webhook URL (not set yet)</b>}</>}
        , signed with the same <span className="ui-mono">X-SuperCash-Signature</span> secret as all SuperCash webhooks.{" "}
        <button className="ui-link" onClick={() => navigate("/callbacks")}>{url ? "Change in Callbacks →" : "Set it in Callbacks →"}</button>
      </span>
    </Banner>
  );
}

/** P2G payments notify through the normal webhook pipeline — show its recent log. */
function RecentWebhooks() {
  // Server-paged (page/page_size → {items,total,...}); the hook reloads on page change.
  const list = usePagedList((p) => api.get("/webhooks/deliveries", { params: p }).then((r) => r.data), {}, 10);
  usePolling(list.load, 5000);
  const rows = list.loading && !list.data.items.length ? null : list.data.items;
  return (
    <Card pad={false} title="Recent webhook deliveries" sub="payment.* and order.paid events, including P2G — same log as the Callbacks page.">
      {rows === null ? <div style={{ padding: 18 }}><div className="ui-skel" style={{ height: 60 }} /></div> : rows.length === 0 ? (
        <Empty icon="🔔" title="No webhooks sent yet">They appear here the moment a payment completes.</Empty>
      ) : (
        <div className="tx-list">
          {rows.map((d) => {
            const ok = d.status_code >= 200 && d.status_code < 300;
            return (
              <div key={d.id} className="tx-row" style={{ cursor: "default", gridTemplateColumns: "14px minmax(0,1.4fr) minmax(0,2fr) 90px 90px" }}>
                <span className="tx-dot" style={{ background: ok ? "#0f7a45" : "#b3261e" }} />
                <span className="tx-id">{d.event}</span>
                <span className="tx-ref" title={d.url}>{d.url}</span>
                <span className="tx-time">{d.latency_ms ? `${d.latency_ms} ms` : "—"}</span>
                <span className="tx-pill" style={{ color: ok ? "#0f7a45" : "#b3261e", background: ok ? "#E3F7EC" : "#FBE9E7" }}>{d.status_code || "error"}</span>
                <span className="tx-mobile">
                  <span className="tx-id">{d.event}</span>
                  <span className="tx-pill" style={{ justifySelf: "end", color: ok ? "#0f7a45" : "#b3261e", background: ok ? "#E3F7EC" : "#FBE9E7" }}>{d.status_code || "error"}</span>
                  <span className="tx-sub" style={{ gridColumn: "1 / -1" }}>{d.url}</span>
                </span>
              </div>
            );
          })}
        </div>
      )}
      <TablePager testId="webhooks-pager" page={list.page} total={list.data.total} size={list.size} onPage={list.setPage} onSize={list.setSize} />
    </Card>
  );
}
